import {Component, DestroyRef, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DxChartModule, DxSelectBoxModule} from 'devextreme-angular';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
    GetOrderPlanningMixScoreQuery,
    GetOrderPlanningMixTrendingQuery,
    MyScoreTrendingGrouping,
    OrdersClient,
} from '@services';
import {DoughnutChartTableComponent, DoughnutChartTableSlice} from '../../../../components/library/doughnut-chart-table/doughnut-chart-table.component';
import {OutboundMyScoreFilterBarComponent, OutboundMyScoreFilters} from '../outbound-my-score-filter-bar/outbound-my-score-filter-bar.component';

type TrendingPoint = { period: Date; loose: number; layer: number; fullPallet: number };

export function orderPlanningMixPalette(): Record<string, string> {
    const getColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();
    return {
        ['Loose']: getColor('--bs-danger'),
        ['Layer']: getColor('--bs-warning'),
        ['Full Pallet']: getColor('--bs-success'),
    };
}

@Component({
    selector: 'app-order-planning-mix',
    imports: [CommonModule, DoughnutChartTableComponent, OutboundMyScoreFilterBarComponent, DxChartModule, DxSelectBoxModule],
    templateUrl: './order-planning-mix.component.html',
})
export class OrderPlanningMixComponent {
    private ordersClient = inject(OrdersClient);
    private destroyRef = inject(DestroyRef);

    protected readonly Loose = 'Loose';
    protected readonly Layer = 'Layer';
    protected readonly FullPallet = 'Full Pallet';

    protected palette: Record<string, string> = {};
    protected looseColor = '';
    protected layerColor = '';
    protected fullPalletColor = '';

    protected grouping = MyScoreTrendingGrouping.Day;
    protected groupingOptions = [
        {value: MyScoreTrendingGrouping.Day, text: 'Day'},
        {value: MyScoreTrendingGrouping.Hour, text: 'Hour'},
    ];

    protected slices: DoughnutChartTableSlice[] = [];
    protected myScorePercent = 0;
    protected trendingData: TrendingPoint[] = [];

    private filters?: OutboundMyScoreFilters;

    constructor() {
        this.palette = orderPlanningMixPalette();
        this.looseColor = this.palette[this.Loose];
        this.layerColor = this.palette[this.Layer];
        this.fullPalletColor = this.palette[this.FullPallet];
    }

    protected onFiltersChanged(filters: OutboundMyScoreFilters): void {
        this.filters = filters;
        this.loadScore();
        this.loadTrending();
    }

    protected onGroupingChanged(): void {
        this.loadTrending();
    }

    protected customizeTrendingTooltip = (info: { seriesName: string; value: number }) => ({
        text: `${info.seriesName}: ${info.value}%`,
    });

    protected percentAxisLabel = (info: { value: number }) => `${info.value}%`;

    private loadScore(): void {
        if (!this.filters) return;

        this.ordersClient
            .getOrderPlanningMixScore(new GetOrderPlanningMixScoreQuery(this.filters))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dto => {
                const totals = dto.totals ?? {};
                this.slices = [this.Loose, this.Layer, this.FullPallet].map(key => ({argument: key, value: totals[key] ?? 0}));
                this.myScorePercent = dto.myScorePercent ?? 0;
            });
    }

    private loadTrending(): void {
        if (!this.filters) return;

        this.ordersClient
            .getOrderPlanningMixTrending(new GetOrderPlanningMixTrendingQuery({...this.filters, grouping: this.grouping}))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dto => {
                this.trendingData = (dto.points ?? []).map(p => ({
                    period: p.period!,
                    loose: p.loosePercent ?? 0,
                    layer: p.layerPercent ?? 0,
                    fullPallet: p.fullPalletPercent ?? 0,
                }));
            });
    }
}
